import moment, { Moment } from 'moment';
import { Dictionary } from 'lodash';
import { Polygon, Geometry } from 'geojson';
import { CollectionTilesResponse, SearchQuery } from '../../../services/Byoc/TileResource';
import { CONFIG } from '../config';
import { Links } from '../api';

export interface ByocCollectionIdentifiable {
  id: string;
}

export type CollectionTypeResponse = 'BATCH' | 'BYOC' | undefined;
export interface NewByocCollection {
  userId: string;
  name: string;
  s3Bucket: string;
  noData?: number;
}

export type ByocCollection = ByocCollectionIdentifiable &
  NewByocCollection & {
    created?: Date;
    additionalData: ByocCollectionAdditionalData;
    type?: CollectionTypeResponse;
  };

export type ByocCollectionWithLocation = ByocCollection & {
  location: string;
};

export type GlobalCollection = {
  id: string;
  userId: string;
  name: string;
  s3Bucket: string;
  location: string;
};

export interface ByocCollectionTilesIdentifiable {
  collectionId: string;
}

export type ByocCollectionTileIdenfitiable = ByocCollectionTilesIdentifiable &
  ByocCollectionIdentifiable;

export interface ByocCollectionAdditionalData {
  bands: Bands;
  maxMetersPerPixel: number;
}

export type Bands = Dictionary<BandFormat>;

export interface BandFormat {
  bitDepth: number;
}

interface ByocTileBase {
  id: string;
  path: string;
  tileGeometry: Polygon;
  coverGeometry: Polygon;
  status: string;
  additionalData: ByocTileAdditionalData;
}

export interface ByocTile extends ByocTileBase {
  sensingTime?: Moment;
}

export interface ByocServerTile extends ByocTileBase {
  sensingTime?: string;
}

export interface ByocTileAdditionalData {
  failedIngestionCause: string;
}

export type NewByocTile = ByocCollectionTilesIdentifiable & {
  path: string;
  sensingTime?: Moment;
};

export type CollectionTilesCacheValue = {
  fetching: boolean;
  query?: SearchQuery;
  tilesPromise?: Promise<CollectionTilesResponse>;
  tiles: ByocTile[];
  links: Links;
};

export type ByocTilesCache = Dictionary<CollectionTilesCacheValue>;

export type ByocCollectionsCache = {
  [index: string]: CollectionCacheRecord;
};

export type CollectionCacheRecord = {
  fetchedState: FetchedState;
  collection: ByocCollectionWithLocation;
  geometry?: Geometry;
};

export enum FetchedState {
  NOT_FETCHED = 'NOT_FETCHED',
  FETCHING = 'FETCHING',
  FETCHING_FAILED = 'FETCHING_FAILED',
  FETCHED = 'FETCHED',
}

export const AWS_EU_CENTRAL_1 = 'aws-eu-central-1';
export const AWS_US_WEST_2 = 'aws-us-west-2';
export const SGS_HQ = 'sgs-hq';
export const MUNDI = 'mundi';
export const CREODIAS = 'creo';
export const CODE_DE = 'codede';

export const LOCATION_NAMES: { [s: string]: string } = {
  [AWS_EU_CENTRAL_1]: 'AWS EU (Frankfurt)',
  [AWS_US_WEST_2]: 'AWS US West (Oregon)',
  [SGS_HQ]: 'Sinergise Test / AWS EU (Frankfurt)',
  [MUNDI]: 'Mundi Web Services',
  [CREODIAS]: 'CREODIAS',
  [CODE_DE]: 'CODE-DE',
};

const LOCATION_CONFIG: { [s: string]: string | undefined } = {
  [AWS_EU_CENTRAL_1]: CONFIG.byocEuCentralServiceBasePath,
  [AWS_US_WEST_2]: CONFIG.byocUsWestServiceBasePath,
  [CODE_DE]: CONFIG.byocCodeDeServiceBasePath,
  [CREODIAS]: CONFIG.byocCreodiasServiceBasePath,
  [MUNDI]: CONFIG.byocMundiServiceBasePath,
  [SGS_HQ]: CONFIG.byocSgsHqServiceBasePath,
};

export const LOCATION_TO_BASE_PATH = (): { [s: string]: string } => {
  const locationToBasePath: { [s: string]: string } = {};

  for (const location in LOCATION_CONFIG) {
    if (LOCATION_CONFIG[location] !== undefined) {
      locationToBasePath[location] = LOCATION_CONFIG[location]!;
    }
  }

  return locationToBasePath;
};

export function mapByocTile(tile: ByocServerTile): ByocTile {
  return {
    ...tile,
    sensingTime: tile.sensingTime ? moment.utc(tile.sensingTime) : undefined,
  };
}
